import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Activity, Server, Database, HardDrive, Zap, Clock,
  AlertTriangle, CheckCircle2, XCircle, RefreshCw, Cpu,
  MemoryStick, FolderOpen, Users, Building2, BarChart3,
  LifeBuoy, Crosshair, Radio,
} from 'lucide-react';
import { getHealthSystem, getHealthDb, getHealthJobs, getHealthStorage, getHealthKpis } from '@/services/apiClient';
import { cn } from '@/lib/utils';

const REFRESH_INTERVAL = 60_000; // 60 seconds

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function fmtUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtTimeAgo(iso) {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Status chip ───────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = {
    ok:       { color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2, label: 'OK' },
    good:     { color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2, label: 'Good' },
    connected:{ color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2, label: 'Connected' },
    warning:  { color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',       icon: AlertTriangle, label: 'Warning' },
    critical: { color: 'bg-red-500/15 text-red-600 dark:text-red-400',             icon: XCircle, label: 'Critical' },
    error:    { color: 'bg-red-500/15 text-red-600 dark:text-red-400',             icon: XCircle, label: 'Error' },
    missed:   { color: 'bg-red-500/15 text-red-600 dark:text-red-400',             icon: XCircle, label: 'Missed' },
    disconnected: { color: 'bg-red-500/15 text-red-600 dark:text-red-400',         icon: XCircle, label: 'Disconnected' },
    unknown:  { color: 'bg-muted text-muted-foreground',                           icon: AlertTriangle, label: 'Unknown' },
  };
  const cfg = map[status] || map.unknown;
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', cfg.color)}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function UsageBar({ pct, className }) {
  const color = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className={cn('h-1.5 w-full rounded-full bg-muted', className)}>
      <div className={cn('h-1.5 rounded-full transition-all', color)} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────
function Card({ title, icon: Icon, children, className }) {
  return (
    <div className={cn('rounded-xl border border-border bg-card p-5', className)}>
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function StatRow({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-right">
        <span className="text-xs font-semibold text-foreground">{value}</span>
        {sub && <span className="ml-1.5 text-[10px] text-muted-foreground">{sub}</span>}
      </div>
    </div>
  );
}

// ── Alert banner ──────────────────────────────────────────────────────────────
function AlertBanner({ alerts }) {
  if (!alerts.length) return null;
  return (
    <div className="mb-5 rounded-xl border border-red-400/40 bg-red-500/10 p-4">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
        <span className="text-sm font-semibold text-red-600 dark:text-red-400">
          {alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}
        </span>
      </div>
      <ul className="space-y-1">
        {alerts.map((a, i) => (
          <li key={i} className="text-xs text-red-600/90 dark:text-red-400/90">• {a}</li>
        ))}
      </ul>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SuperAdminHealthPage() {
  const [data, setData] = useState({ system: null, db: null, jobs: null, storage: null, kpis: null });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [errors, setErrors] = useState({});
  const timerRef = useRef(null);

  const fetchAll = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    const results = await Promise.allSettled([
      getHealthSystem(),
      getHealthDb(),
      getHealthJobs(),
      getHealthStorage(),
      getHealthKpis(),
    ]);
    const keys = ['system', 'db', 'jobs', 'storage', 'kpis'];
    const newData = {};
    const newErrors = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') newData[keys[i]] = r.value;
      else newErrors[keys[i]] = r.reason?.message || 'Failed';
    });
    setData(prev => ({ ...prev, ...newData }));
    setErrors(newErrors);
    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll(true);
    timerRef.current = setInterval(() => fetchAll(false), REFRESH_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchAll]);

  // Derive alerts
  const alerts = [];
  const sys = data.system;
  const db  = data.db;
  const jobs = data.jobs?.jobs || [];

  if (sys?.disk?.usedPercent >= 90) alerts.push(`Disk at ${sys.disk.usedPercent}% — critical`);
  else if (sys?.disk?.usedPercent >= 85) alerts.push(`Disk at ${sys.disk.usedPercent}% — approaching limit`);
  if (sys?.memory?.usedPercent >= 85) alerts.push(`RAM at ${sys.memory.usedPercent}% — high pressure`);
  if (db && !db.connected) alerts.push('MongoDB is disconnected');
  if (db?.stats?.pendingTranscriptions > 10) alerts.push(`${db.stats.pendingTranscriptions} transcriptions stuck in queue`);
  jobs.filter(j => j.isMissed).forEach(j => alerts.push(`Cron job "${j.label}" has not run in ${j.minutesSinceRun}m`));

  const memPct  = sys?.memory?.usedPercent ?? 0;
  const diskPct = sys?.disk?.usedPercent ?? 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">System Health</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Live platform monitoring · Auto-refreshes every 60s
            {lastRefresh && ` · Last: ${fmtTimeAgo(lastRefresh)}`}
          </p>
        </div>
        <button
          onClick={() => fetchAll(false)}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Live indicator */}
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <span className="text-[11px] text-muted-foreground">Live monitoring active</span>
      </div>

      {/* Alerts */}
      <AlertBanner alerts={alerts} />

      {loading && !data.system ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          Loading health data…
        </div>
      ) : (
        <>
          {/* Top KPI strip */}
          {data.kpis && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { icon: Building2, label: 'Companies',      value: data.kpis.companies?.total ?? '—' },
                { icon: Users,     label: 'Users',          value: data.kpis.users?.total ?? '—' },
                { icon: BarChart3, label: 'Interviews Today',value: data.kpis.interviews?.today ?? '—' },
                { icon: LifeBuoy,  label: 'Open Tickets',   value: data.kpis.support?.openTickets ?? '—' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="rounded-xl border border-border bg-card px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground">{label}</span>
                  </div>
                  <span className="text-2xl font-bold text-foreground">{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Main grid */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">

            {/* Server Vitals */}
            <Card title="Server Vitals" icon={Server}>
              {sys ? (
                <div className="space-y-1">
                  <StatRow label="Hostname" value={sys.server.hostname} />
                  <StatRow label="Platform" value={`${sys.server.platform} / ${sys.server.arch}`} />
                  <StatRow label="Node.js" value={sys.server.nodeVersion} />
                  <StatRow label="CPUs" value={`${sys.server.cpuCount}× ${sys.server.cpuModel?.split(' ').slice(0,3).join(' ')}`} />
                  <StatRow
                    label="Load Avg"
                    value={`${sys.server.loadAvg['1m']} / ${sys.server.loadAvg['5m']} / ${sys.server.loadAvg['15m']}`}
                    sub="1m/5m/15m"
                  />
                  <StatRow label="OS Uptime"   value={fmtUptime(sys.server.osUptime)} />
                  <StatRow label="Node Uptime" value={fmtUptime(sys.server.nodeUptime)} />
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{errors.system || 'Unavailable'}</p>
              )}
            </Card>

            {/* Memory */}
            <Card title="Memory" icon={MemoryStick}>
              {sys?.memory ? (
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">RAM</span>
                      <span className={cn('font-semibold', memPct >= 85 ? 'text-red-500' : memPct >= 70 ? 'text-amber-500' : 'text-emerald-600')}>
                        {memPct}%
                      </span>
                    </div>
                    <UsageBar pct={memPct} />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                      <span>{fmtBytes(sys.memory.used)} used</span>
                      <span>{fmtBytes(sys.memory.total)} total</span>
                    </div>
                  </div>
                  {sys.memory.swap?.total > 0 && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Swap</span>
                        <span className="font-semibold">{sys.memory.swap.usedPercent}%</span>
                      </div>
                      <UsageBar pct={sys.memory.swap.usedPercent} />
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                        <span>{fmtBytes(sys.memory.swap.used)} used</span>
                        <span>{fmtBytes(sys.memory.swap.total)} total</span>
                      </div>
                    </div>
                  )}
                  <div className="border-t border-border/50 pt-2 space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Node Heap</p>
                    <StatRow label="Heap Used"  value={fmtBytes(sys.heap?.heapUsed)} />
                    <StatRow label="Heap Total" value={fmtBytes(sys.heap?.heapTotal)} />
                    <StatRow label="RSS"        value={fmtBytes(sys.heap?.rss)} />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{errors.system || 'Unavailable'}</p>
              )}
            </Card>

            {/* Disk */}
            <Card title="Disk" icon={HardDrive}>
              {sys?.disk && !sys.disk.error ? (
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Root (/)</span>
                      <span className={cn('font-semibold', diskPct >= 90 ? 'text-red-500' : diskPct >= 80 ? 'text-amber-500' : 'text-emerald-600')}>
                        {diskPct}%
                      </span>
                    </div>
                    <UsageBar pct={diskPct} />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                      <span>{fmtBytes(sys.disk.used)} used</span>
                      <span>{fmtBytes(sys.disk.total)} total · {fmtBytes(sys.disk.avail)} free</span>
                    </div>
                  </div>
                  {diskPct >= 85 && (
                    <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-2 text-[11px] text-red-600 dark:text-red-400">
                      Disk critically full — free up space or expand volume
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{sys?.disk?.error || errors.system || 'Unavailable'}</p>
              )}
            </Card>

            {/* Database */}
            <Card title="Database" icon={Database}>
              {db ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Status</span>
                    <StatusChip status={db.connected ? 'connected' : 'disconnected'} />
                  </div>
                  {db.stats?.counts && (
                    <>
                      <StatRow label="Sessions"       value={db.stats.counts.interviewsessions?.toLocaleString() ?? '—'} />
                      <StatRow label="Responses"      value={db.stats.counts.interviewresponses?.toLocaleString() ?? '—'} />
                      <StatRow label="Companies"      value={db.stats.counts.companies?.toLocaleString() ?? '—'} />
                      <StatRow label="Users"          value={db.stats.counts.users?.toLocaleString() ?? '—'} />
                      <StatRow label="Prospect Leads" value={db.stats.counts.prospectleads?.toLocaleString() ?? '—'} />
                      <StatRow label="Support Tickets"value={db.stats.counts.supporttickets?.toLocaleString() ?? '—'} />
                      <div className="border-t border-border/50 mt-2 pt-2 space-y-1">
                        <StatRow
                          label="Live Sessions"
                          value={db.stats.inProgress ?? '—'}
                          sub="in_progress"
                        />
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-xs text-muted-foreground">Pending Transcriptions</span>
                          <span className={cn('text-xs font-semibold', (db.stats.pendingTranscriptions || 0) > 10 ? 'text-red-500' : 'text-foreground')}>
                            {db.stats.pendingTranscriptions ?? '—'}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{errors.db || 'Unavailable'}</p>
              )}
            </Card>

            {/* Cron Jobs */}
            <Card title="Background Jobs" icon={Zap}>
              {data.jobs?.jobs?.length ? (
                <div className="space-y-3">
                  {data.jobs.jobs.map((job) => {
                    const chipStatus = job.isMissed ? 'missed' : job.lastStatus === 'error' ? 'error' : job.lastStatus === 'ok' ? 'ok' : 'unknown';
                    return (
                      <div key={job.key} className="rounded-lg border border-border/50 p-3">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className="text-xs font-medium text-foreground leading-tight">{job.label}</span>
                          <StatusChip status={chipStatus} />
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 text-[10px] text-muted-foreground">
                          <span>Every {job.intervalMin >= 60 ? `${job.intervalMin / 60}h` : `${job.intervalMin}m`}</span>
                          <span className="text-right">{job.lastRun ? fmtTimeAgo(job.lastRun) : 'Never run'}</span>
                        </div>
                        {job.lastError && (
                          <p className="mt-1 text-[10px] text-red-500 truncate">{job.lastError}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{errors.jobs || 'No job data yet (resets on restart)'}</p>
              )}
            </Card>

            {/* Storage */}
            <Card title="File Storage" icon={FolderOpen}>
              {data.storage?.directories ? (
                <div className="space-y-3">
                  {data.storage.directories.map((dir) => (
                    <div key={dir.key} className="rounded-lg border border-border/50 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-foreground">{dir.label}</span>
                        {!dir.exists && <StatusChip status="warning" />}
                      </div>
                      <div className="text-[10px] text-muted-foreground space-y-0.5">
                        <div className="flex justify-between">
                          <span>Files</span>
                          <span className="font-semibold text-foreground">{dir.fileCount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Size</span>
                          <span className="font-semibold text-foreground">{fmtBytes(dir.totalBytes)}</span>
                        </div>
                        <div className="text-[9px] text-muted-foreground/60 truncate">{dir.path}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{errors.storage || 'Unavailable'}</p>
              )}
            </Card>

          </div>

          {/* Interview KPIs row */}
          {data.kpis?.interviews && (
            <Card title="Interview Activity" icon={Activity}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: 'Today',     value: data.kpis.interviews.today },
                  { label: 'This Week', value: data.kpis.interviews.week },
                  { label: 'This Month',value: data.kpis.interviews.month },
                  { label: 'All Time',  value: data.kpis.interviews.allTime },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-lg border border-border/50 p-3 text-center">
                    <p className="text-2xl font-bold text-foreground">{value?.toLocaleString() ?? '—'}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
